import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Select from 'react-select';
import Papa from 'papaparse';
import Modal from './Modal';
import mediaCsvFile from '../media.csv';

const PrintPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const fileDetails = location.state?.fileDetails;
    const { name, size, pageCount, width, height } = fileDetails;
    const [selectedOption, setSelectedOption] = useState(null);
    const [nextSelectedOption, setNextSelectedOption] = useState({ label: null, width: null, price: null});
    const [error, setError] = useState(false);
    const [numCopies, setNumCopies] = useState(1);
    const price = (nextSelectedOption.price * nextSelectedOption.width * height/72 * numCopies).toFixed(2);
    const printTypes = {
        'color-laser': 'Color Laser',
        'large-format': 'Large Format',
        'roland': 'Roland',
        'bristol-print': 'Bristol Print',
        'booklet': 'Booklet',
    };
    const [largeFormatOptions, setLargeFormatOptions] = useState(null);
    const [colorLaserOptions, setColorLaserOptions] = useState([
        { id: 1, text: '8.5 x 11', selected: false, side: '', width: 8.5 },
        { id: 2, text: '11 x 17 ', selected: false, side: '', width: 11 },
        { id: 3, text: '12 x 18 ', selected: false, side: '', width: 12 },
    ]);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('user'));
        if (!user) {
            navigate('/login');
        }

        const fetchLargeFormatData = async () => {
            const response = await fetch(mediaCsvFile);
            const csvData = await response.text();

            Papa.parse(csvData, {
                header: true,
                complete: (result) => {
                    const parsedOptions = result.data.map((row) => ({
                        label: `${row['PAPER MEDIA']} W${row['WIDTH']}`,
                        widthValue: row['WIDTH'],
                        price: row['MULTIPLIER'],
                    }));
                    setLargeFormatOptions(parsedOptions);
                },
            });
        };

        fetchLargeFormatData();
    }, [navigate]);
    
    if (!fileDetails) {
        return <div>No file details provided.</div>;
    };

    const handleOptionChange = (e) => {
        setSelectedOption(e.target.value);
        setNextSelectedOption({ label: null, width: null, price: 0});
        setError(false);
    };

    const handle1sChange = (index) => {
        const updatedOptions = colorLaserOptions.map((option, idx) => ({
            ...option,
            selected: idx === index,
            side: idx === index ? '1 sided' : option.side,
        }));
        setColorLaserOptions(updatedOptions);
        setNextSelectedOption({label: `${updatedOptions[index].text}, 1 sided`, width: updatedOptions[index].width, price: 0});
    };
    const handle2sChange = (index) => {
        const updatedOptions = colorLaserOptions.map((option, idx) => ({
            ...option,
            selected: idx === index,
            side: idx === index ? '2 sided' : option.side,
        }));
        setColorLaserOptions(updatedOptions);
        setNextSelectedOption({label: `${updatedOptions[index].text}, 2 sided`, width: updatedOptions[index].width, price: 0});
    };

    const handleLargeFormatOptionChange = (nextSelectedOption) => {
        setNextSelectedOption({ label: nextSelectedOption.label, width: nextSelectedOption.widthValue, price: nextSelectedOption.price});
    };

    const handleBookletOptionChange = (e) => {
        setNextSelectedOption({label: e.target.value, width: width/72, price: 0});

        if (pageCount % 4 !== 0) {
            setError(true);
        } else {
            setError(false);
        }
    };

    const handleNumCopiesChange = (event) => {
        setNumCopies(event.target.value);
    };

    const handleSubmit = () => {
        console.log('Printing...');
        navigate('/submit');
    };

    return (
        <div>
            <button style={styles.backButton} onClick={() => navigate(-1)}>&lt; Back</button>
            <p style={styles.fileName}>{name}</p>
            <p><strong>&emsp;File Size:</strong> {(size / 1024 / 1024).toFixed(4)} MB</p>
            <p><strong>&emsp;Page Count:</strong> {pageCount}</p>
            <p><strong>&emsp;Width:</strong> {width / 72} in</p>
            <p><strong>&emsp;Height:</strong> {height / 72} in</p>
            <br />
            <label>
                <input
                    type="radio"
                    name="printingOptions"
                    value="color-laser"
                    checked={selectedOption === 'color-laser'}
                    onChange={handleOptionChange}
                />
                Color Laser
            </label><br />
            <label>
                <input
                    type="radio"
                    name="printingOptions"
                    value="large-format"
                    checked={selectedOption === 'large-format'}
                    onChange={handleOptionChange}
                />
                Large Format
            </label><br />
            <label>
                <input
                    type="radio"
                    name="printingOptions"
                    value="roland"
                    checked={selectedOption === 'roland'}
                    onChange={handleOptionChange}
                />
                Roland
            </label><br />
            <label>
                <input
                    type="radio"
                    name="printingOptions"
                    value="bristol-print"
                    checked={selectedOption === 'bristol-print'}
                    onChange={handleOptionChange}
                />
                Bristol Print
            </label><br />
            <label>
                <input
                    type="radio"
                    name="printingOptions"
                    value="booklet"
                    checked={selectedOption === 'booklet'}
                    onChange={handleOptionChange}
                />
                Booklet
            </label><br />
            <br />
            {selectedOption && (
                <>
                    <h2>{printTypes[selectedOption]}</h2>
                    <div style={styles.printOptions}>
                        <div style={styles.printContainer}>
                            {selectedOption === 'color-laser' ? (
                                <div>
                                    <p>&emsp;&emsp;&emsp;&ensp;1-sided &ensp;  2-sided</p>
                                    {colorLaserOptions.map((option, index) => (
                                        <div key={option.id}>
                                            {option.text}
                                            <label className="colorLaserOptions">
                                                <input
                                                    type="radio"
                                                    name={`colorLaserOptions${index}`}
                                                    checked={option.selected && option.side === '1 sided'}
                                                    onChange={() => handle1sChange(index)}
                                                />{' '}
                                            </label>
                                            <label className="colorLaserOptions">
                                                <input
                                                    type="radio"
                                                    name={`colorLaserOptions${index}`}
                                                    checked={option.selected && option.side === '2 sided'}
                                                    onChange={() => handle2sChange(index)}
                                                />{' '}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            ) : selectedOption === 'large-format' ? (
                                <div>
                                    <Select
                                        value={nextSelectedOption.label ? { label: nextSelectedOption.label, value: nextSelectedOption.label } : null}
                                        onChange={handleLargeFormatOptionChange}
                                        options={largeFormatOptions}
                                        placeholder="Select an option..."
                                        isSearchable={true}
                                    />
                                </div>
                            ) : selectedOption === 'roland' ? (
                                <div>
                                    <p>Roland Printing will be available soon.</p>
                                </div>
                            ) : selectedOption === 'bristol-print' ? (
                                <div>
                                    <p>30 x 38</p>
                                    <p>$9 per sheet</p>
                                </div>
                            ) : (
                                <div>
                                    <label>
                                        <input
                                            type="radio"
                                            name="bookletOptions"
                                            value="small-booklet"
                                            checked={nextSelectedOption.label === 'small-booklet'}
                                            onChange={handleBookletOptionChange}
                                        />
                                        Small Booklet
                                    </label><br />
                                    <label>
                                        <input
                                            type="radio"
                                            name="bookletOptions"
                                            value="large-booklet"
                                            checked={nextSelectedOption.label === 'large-booklet'}
                                            onChange={handleBookletOptionChange}
                                        />
                                        Large Booklet
                                    </label><br />

                                </div>

                            )}
                            <br />
                            <label htmlFor="copies"><b>Number of Copies: </b></label>
                            <input type="number" value={numCopies} className="num-copies" onChange={handleNumCopiesChange} min="1" />
                            <br /><b>Selected Option:</b> {nextSelectedOption.label}
                            <br /><b>Width:</b> {nextSelectedOption.width} in.
                        </div>
                        <div style={styles.priceContainer}>
                            <br />
                            <p style={styles.price}>
                                <b>Total Price:</b> ${price}
                            </p>
                            {error &&
                                <p style={{ color: 'red' }}>Error: Page count must be a multiple of 4 for booklet options.</p>
                            }
                            <button id="submit-button" onClick={() => setIsModalOpen(true)} disabled={error || !nextSelectedOption.label}>Confirm and Print</button>
                        </div>
                    </div>
                </>
            )}
            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
                <h3>Are you sure you want to print?</h3>
                <p>Price: ${price}</p>
                <div style={styles.buttonContainer}>
                    <button 
                        style={styles.cancelButton}
                        onClick={() => setIsModalOpen(false)}
                    >
                        Cancel
                    </button>
                    <button 
                        style={styles.confirmButton}
                        onClick={handleSubmit}
                    >
                        Yes
                    </button>
                </div>
            </Modal>
        </div>
    )
};

const styles = {
    backButton: {
        color: '#FFB81C',
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        fontSize: '16px',
    },
    fileName: {
        fontWeight: 'bold',
        fontSize: '20px',
        color: '#333333'
    },
    printOptions: {
        display: 'flex',
        justifyContent: 'space-between',
        // space between containers
        gap: '20px',
    },
    printContainer: {
        width: '50%',
    },
    priceContainer: {
        width: '50%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    price: {
        fontSize: '20px',
    },
    // dialog styles
    dialog: {
        padding: '20px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '10px',
        marginTop: '20px'
    },
    confirmButton: {
        padding: '8px 16px',
        backgroundColor: '#FFB81C',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer'
    },
    cancelButton: {
        padding: '8px 16px',
        backgroundColor: '#f5f5f5',
        border: '1px solid #ddd',
        borderRadius: '4px',
        cursor: 'pointer'
    },
}

export default PrintPage;